import { useQuery } from '@tanstack/react-query';
import { ChevronRight, CircleCheck, Scale } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { getConciliacion, type InventarioRow, type VentaBucket } from '../api';
import { getConciliacionUnidades } from '../stockUnidades';

const BUCKET_LABEL: Record<VentaBucket, string> = {
  reservado: 'Pago Pendiente',
  reservadoParcial: 'Pago Parcial',
  pendienteDespacho: 'Pendiente Despacho',
};

export const conciliacionQueryKey = (pais: string) => ['inventario', 'conciliacion', pais] as const;

/** Descuadres del país activo; lo comparten el botón de la página y el diálogo. */
export function useConciliacion(enabled: boolean) {
  const { config: activeCountry } = useActiveCountry();
  return useQuery({
    queryKey: conciliacionQueryKey(activeCountry.code),
    queryFn: getConciliacion,
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Artículos cuyos buckets de ventas (Pago Pendiente / Pago Parcial /
 * Pendiente Despacho) no coinciden con lo que retienen las ventas abiertas.
 * Sólo muestra: se corrige abriendo el artículo → Editar → "Igualar a…".
 */
export function ConciliacionDialog({
  onSelect,
  onClose,
}: {
  onSelect: (row: InventarioRow) => void;
  onClose: () => void;
}) {
  const q = useConciliacion(true);
  const rows = q.data?.rows ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-4 w-4" aria-hidden />
            Descuadres de inventario
          </DialogTitle>
          <DialogDescription>
            Buckets de ventas que no coinciden con lo que tienen las ventas abiertas.
            Para corregir uno, abrí el artículo → Editar → "Igualar a…".
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-2">
          {q.isPending ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : q.isError ? (
            <p className="py-6 text-center text-sm text-destructive">
              No se pudieron calcular los descuadres. Probá de nuevo.
            </p>
          ) : rows.length === 0 ? (
            <p className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <CircleCheck className="h-4 w-4 text-emerald-600" aria-hidden />
              Todo cuadra: cada bucket de ventas coincide con las ventas abiertas.
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Diferencia positiva: unidades que no son de ninguna venta. Negativa: a las ventas
                les faltan unidades.
              </p>
              <ul className="space-y-2">
                {rows.map(({ row, descuadres }) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(row)}
                      className="group flex w-full items-start gap-3 rounded-xl border bg-card px-3 py-2.5 text-left transition-colors hover:border-foreground/30 hover:bg-muted/40"
                    >
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <p className="truncate font-medium text-foreground">
                          {row.fields.Articulo_I || 'Artículo sin nombre'}
                          {row.modelo && (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                              {row.modelo}
                            </span>
                          )}
                        </p>
                        {descuadres.map((d) => (
                          <div
                            key={d.bucket}
                            className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm"
                          >
                            <span className="w-36 shrink-0 text-muted-foreground">
                              {BUCKET_LABEL[d.bucket]}
                            </span>
                            <span className="tabular-nums">
                              inventario <strong>{fmt(d.inventario)}</strong> · ventas{' '}
                              <strong>{fmt(d.ventas)}</strong>
                            </span>
                            <span
                              className={cn(
                                'rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                                d.diferencia > 0
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-destructive/10 text-destructive',
                              )}
                            >
                              {d.diferencia > 0 ? '+' : ''}
                              {fmt(d.diferencia)}
                            </span>
                            {d.ventaIds.length > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {d.ventaIds.length === 1 ? 'venta' : 'ventas'}{' '}
                                {d.ventaIds.join(', ')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      <ChevronRight
                        className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                        aria-hidden
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <UnidadesVsContadores />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Stock por origen vs contadores: por artículo, la suma de las unidades contra
 * lo que dice Inventario (en depósito = disponible + reservas de ventas;
 * prestado = Prest/Show). Sólo muestra; se corrige con Editar, que mueve las dos cosas.
 */
function UnidadesVsContadores() {
  const { config: activeCountry } = useActiveCountry();
  const q = useQuery({
    queryKey: ['inventario', 'unidades-conciliacion', activeCountry.code],
    queryFn: getConciliacionUnidades,
    staleTime: 60_000,
  });
  if (q.isPending || q.isError || !q.data.habilitado) return null;
  const { resumen, diferencias, unidadesDescuadradas } = q.data;
  return (
    <section className="space-y-2 border-t pt-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Stock detallado vs contadores
      </h3>
      {diferencias.length === 0 && unidadesDescuadradas.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <CircleCheck className="h-4 w-4 text-emerald-600" aria-hidden />
          Las unidades cuadran con los contadores ({resumen.articulos} artículos, {resumen.unidadesAuditadas} filas).
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {resumen.conDiferencia} artículo(s) donde las unidades no suman lo que dicen los contadores
            {unidadesDescuadradas.length > 0
              ? `; ${unidadesDescuadradas.length} unidad(es) no cuadran con sus movimientos`
              : ''}
            . Diferencia = unidades − contadores.
          </p>
          <ul className="space-y-1.5">
            {diferencias.map((d) => (
              <li key={d.articleId} className="rounded-lg border bg-card px-3 py-2 text-sm">
                <p className="font-medium">{d.articulo}</p>
                <p className="text-xs tabular-nums text-muted-foreground">
                  En depósito: contadores {fmt(d.deposito.contadores)} · unidades {fmt(d.deposito.unidades)} (
                  {d.deposito.diferencia > 0 ? '+' : ''}
                  {fmt(d.deposito.diferencia)})
                  {(d.prestado.contadores > 0 || d.prestado.unidades > 0) && (
                    <>
                      {' · '}Prestado: contadores {fmt(d.prestado.contadores)} · unidades {fmt(d.prestado.unidades)} (
                      {d.prestado.diferencia > 0 ? '+' : ''}
                      {fmt(d.prestado.diferencia)})
                    </>
                  )}
                </p>
                {d.nota && <p className="text-xs text-amber-700">{d.nota}</p>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function fmt(n: number): string {
  return String(Math.round(n * 100) / 100);
}
